import { useNavigate, createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from "react"
import { useAuth } from "@/lib/auth-context"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { API_BASE } from '@/lib/api-base'

function LoginBrandPanel() {
  return (
    <div className="relative shrink-0 overflow-hidden bg-primary py-6 px-5 text-primary-foreground md:flex md:min-h-screen md:w-[45%] md:flex-col md:justify-center md:p-12 lg:w-[42%]">
      <div
        className="pointer-events-none absolute inset-0 text-primary-foreground opacity-[0.07]"
        aria-hidden
      >
        <svg className="h-full w-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="login-grid" width="32" height="32" patternUnits="userSpaceOnUse">
              <path
                d="M32 0H0V32"
                fill="none"
                stroke="currentColor"
                strokeWidth="0.5"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#login-grid)" />
        </svg>
      </div>
      <div className="relative z-10 max-w-lg">
        <p className="text-xs font-semibold uppercase tracking-widest text-primary-foreground/80 md:text-sm">
          Bem-vindo
        </p>
        <h1 className="mt-1 text-2xl font-bold tracking-tight md:mt-2 md:text-4xl lg:text-5xl">
          GranjaSmart
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-primary-foreground/90 md:mt-4 md:text-lg md:leading-snug">
          Produção, estoque e financeiro da sua granja em um só lugar.
        </p>
      </div>
    </div>
  )
}

function LoginPage() {
  const [email, setEmail] = useState("")
  const [senha, setSenha] = useState("")
  const [error, setError] = useState(() => {
    const message = sessionStorage.getItem("auth_error") || ""
    sessionStorage.removeItem("auth_error")
    return message
  })
  const [loading, setLoading] = useState(false)
  const { login, user, isLoading: authLoading } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (!authLoading && user) {
      void navigate({ to: '/dashboard', replace: true })
    }
  }, [authLoading, user, navigate])

  const apiBase = API_BASE
  const validateForm = () => {
    if (!email.trim()) {
      return "Email e obrigatorio"
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      return "Email invalido"
    }
    if (!senha) {
      return "Senha e obrigatoria"
    }
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")

    const validationError = validateForm()
    if (validationError) {
      setError(validationError)
      return
    }

    setLoading(true)

    try {
      const res = await fetch(`${apiBase}/api/auth/login`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, senha }),
      })

      if (res.ok) {
        const ok = await login()
        if (ok) {
          void navigate({ to: "/dashboard" })
        } else {
          setError("Nao foi possivel iniciar a sessao.")
        }
      } else {
        let errorMessage = "Erro ao fazer login"
        try {
          const data = await res.json()
          errorMessage = data.detail || data.message || errorMessage
        } catch {
          errorMessage = "Email ou senha invalidos"
        }
        setError(errorMessage)
      }
    } catch {
      setError("Erro de conexao. Verifique sua internet.")
    }

    setLoading(false)
  }

  return (
    <main className="flex min-h-screen flex-col bg-background md:flex-row">
      <LoginBrandPanel />

      <div className="flex flex-1 flex-col items-center justify-center px-4 py-6 md:py-10">
        <Card className="w-full max-w-md animate-in fade-in slide-in-from-bottom-3 duration-300 ease-out motion-reduce:animate-none motion-reduce:opacity-100">
          <CardHeader>
            <CardTitle className="text-center">Entrar</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="senha">Senha</Label>
                <Input
                  id="senha"
                  type="password"
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  required
                  autoComplete="current-password"
                />
              </div>
              {error && <p className="text-destructive text-sm">{error}</p>}
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? "Entrando..." : "Entrar"}
              </Button>
            </form>
            <p className="text-center text-sm text-muted-foreground mt-4">
              Ainda nao tem conta?{" "}
              <Button variant="link" onClick={() => void navigate({ to: "/register" })}>
                Registrar
              </Button>
            </p>
          </CardContent>
        </Card>
      </div>
    </main>
  )
}

export const Route = createFileRoute('/login')({
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => ({
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
  }),
  component: LoginPage,
})
